import { searchActorInTMDB, searchDirector } from './movieApi';

// Extract genre terms from a message
export function extractGenreTerms(message) {
  const genreKeywords = [
    'action', 'comedy', 'drama', 'horror', 'thriller', 'romance', 'romantic',
    'sci-fi', 'science fiction', 'fantasy', 'adventure', 'animation', 'animated',
    'documentary', 'mystery', 'crime', 'family', 'kids', 'children', 'musical',
    'music', 'war', 'western', 'history', 'historical', 'biography', 'biographical',
    'superhero', 'anime', 'cartoon'
  ];
  
  const words = message.toLowerCase().split(/[\s,]+/);
  const foundGenres = [];
  
  // Look for genre keywords in the message
  for (const word of words) {
    if (genreKeywords.includes(word)) {
      foundGenres.push(word);
    }
  }
  
  // Also check for two-word genres
  for (let i = 0; i < words.length - 1; i++) {
    const twoWordGenre = words[i] + ' ' + words[i + 1];
    if (genreKeywords.includes(twoWordGenre)) {
      foundGenres.push(twoWordGenre);
    }
  }
  
  return foundGenres.length > 0 ? foundGenres.join(' ') : null;
}

// Extract time period or year from a message
export function extractYearOrEra(message) {
  const cleanedMessage = message.toLowerCase();
  
  // Check for specific year
  const yearMatch = cleanedMessage.match(/\b(19|20)\d{2}\b/);
  if (yearMatch) return { type: 'year', value: yearMatch[0], start: yearMatch[0], end: yearMatch[0] };

  // Check for decades
  const decadeMatch = cleanedMessage.match(/\b(19|20)\d0s\b/);
  if (decadeMatch) {
    const decade = decadeMatch[0].replace('s', '');
    return { 
      type: 'decade', 
      start: decade,
      end: (parseInt(decade) + 9).toString()
    };
  }

  // Check for eras
  const eraMap = {
    'classic': { start: '1920', end: '1959' },
    'golden age': { start: '1930', end: '1959' },
    'vintage': { start: '1920', end: '1969' },
    'old': { start: '1920', end: '1969' },
    'modern': { start: '2000', end: new Date().getFullYear().toString() },
    'contemporary': { start: '2010', end: new Date().getFullYear().toString() },
    'recent': { start: '2020', end: new Date().getFullYear().toString() },
    'new': { start: '2020', end: new Date().getFullYear().toString() },
    '80s': { start: '1980', end: '1989' },
    '90s': { start: '1990', end: '1999' },
    '2000s': { start: '2000', end: '2009' },
    '2010s': { start: '2010', end: '2019' },
    '2020s': { start: '2020', end: new Date().getFullYear().toString() }
  };

  for (const [era, range] of Object.entries(eraMap)) {
    if (cleanedMessage.includes(era)) {
      return { type: 'era', ...range };
    }
  }

  return null;
}

// Check if there's an actor mentioned
export async function checkIfActor(message) {
  try {
    const cleanedMessage = message.toLowerCase().trim();
    
    // Check for actor-specific patterns
    const actorPatterns = [
      /^([^0-9]+?)\s*(?:movies?|films?)/i,  // "Actor Name movies"
      /(?:starring|with|actor|actress)\s+([^0-9]+?)(?:\s+in|\s*$)/i,  // "movies with Actor Name"
      /movies?\s+(?:starring|with|by|featuring)\s+([^0-9]+?)(?:\s+in|\s*$)/i  // "movies starring Actor Name"
    ];

    for (const pattern of actorPatterns) {
      const match = cleanedMessage.match(pattern);
      if (match) {
        const name = match[1].trim();
        if (name.length > 2 && !isCommonNonPersonWord(name)) {
          const actor = await searchActorInTMDB(name);
          if (actor) return actor;
        }
      }
    }

    return null;
  } catch (error) {
    console.error('Actor check error:', error);
    return null;
  }
}

// Check if there's a director mentioned
export async function checkIfDirector(message) {
  try {
    const cleanedMessage = message.toLowerCase();
    
    // Check for explicit director mention
    const directorPatterns = [
      /directed by\s+([^.!?,]+)/i,
      /director[:\s]+([^.!?,]+)/i,
      /films? by\s+([^.!?,]+)/i,
      /([^.!?,\s]+(?:\s+[^.!?,\s]+){0,2})'s\s+(?:movies?|films?)/i  // "Nolan's movies"
    ];

    for (const pattern of directorPatterns) {
      const match = cleanedMessage.match(pattern);
      if (match) {
        const directorName = match[1].trim();
        if (directorName.length >= 3 && !isCommonNonPersonWord(directorName)) {
          const director = await searchDirector(directorName);
          if (director) return director;
        }
      }
    }
    
    return null;
  } catch (error) {
    console.error('Director check error:', error);
    return null;
  }
}

// Check if the word is a common non-person word
function isCommonNonPersonWord(text) {
  const commonWords = [
    'action', 'comedy', 'drama', 'horror', 'thriller',
    'movie', 'movies', 'film', 'films', 'show', 'shows',
    'good', 'best', 'popular', 'top', 'great', 'that', 'some',
    'from', 'with', 'like', 'about', 'any', 'all'
  ];
  return commonWords.includes(text.toLowerCase());
}

// Parse a user message to extract search criteria
export async function parseUserMessage(message) {
  const cleanedMessage = message.toLowerCase();
  let searchCriteria = {
    actor: null,
    director: null,
    year: null,
    genre: null,
    title: null,
    rating: null
  };

  // Extract time periods/eras
  const timeframe = extractYearOrEra(message);
  if (timeframe) {
    searchCriteria.year = timeframe;
  }

  // Check for specific title search
  if (cleanedMessage.includes('movie called') || cleanedMessage.includes('titled')) {
    const titleMatch = cleanedMessage.match(/(?:movie called|titled)\s*["']?([^"']+)["']?/i);
    if (titleMatch) {
      searchCriteria.title = titleMatch[1].trim();
    }
  }

  // Check for actor
  const actor = await checkIfActor(message);
  if (actor) {
    searchCriteria.actor = actor;
  }

  // Check for director
  const director = await checkIfDirector(message);
  if (director) {
    searchCriteria.director = director;
  }

  // Check for genre
  const genreTerms = extractGenreTerms(cleanedMessage);
  if (genreTerms) {
    searchCriteria.genre = genreTerms;
  }

  // If no criteria matched at all, use the whole message as genre
  if (!searchCriteria.actor && !searchCriteria.director && 
      !searchCriteria.year && !searchCriteria.genre && !searchCriteria.title) {
    searchCriteria.genre = cleanedMessage;
  }
  
  return searchCriteria;
}
